import React, { useCallback } from "react";
import { Row, Col, DatePicker } from "antd";
import { useTranslation } from "react-i18next";
import { handleRangePickerChange } from "/app/src/helpers";
import HeroButton from "/app/src/components/HeroUi/Button";
import { Search } from "/app/src/components/generic/components/search";

const { RangePicker } = DatePicker;
type RangeValue = Parameters<
  NonNullable<React.ComponentProps<typeof DatePicker.RangePicker>["onChange"]>
>[0];
/**
 * Renders the inner controls component for filtering transactions.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {number} props.count - The count of transactions.
 * @param {(searchString: string) => void} props.setSearchString - The function to set the search string.
 * @param {() => void} props.updateTransactions - The function to update transactions.
 * @param {string[]} props.ordersRetrying - The array of orders retrying.
 * @param {() => void} props.refetch - The function to refetch transactions.
 * @param {(time: string) => void} props.setAfterTime - The function to set the after time.
 * @param {(time: string) => void} props.setBeforeTime - The function to set the before time.
 * @returns {JSX.Element} The rendered InnerControls component.
 */
export default function InnerControls({
  count,
  setSearchString,
  updateTransactions,
  ordersRetrying,
  refetch,
  setAfterTime,
  setBeforeTime,
}: {
  count: number;
  setSearchString: (searchString: string) => void;
  updateTransactions: () => void;
  ordersRetrying: string[];
  refetch: () => void;
  setAfterTime: (time: string) => void;
  setBeforeTime: (time: string) => void;
}) {
  const { t } = useTranslation();
  //handler for user changing the range of dates
  const onOk = useCallback(
    (value: RangeValue) => {
      handleRangePickerChange(value, setBeforeTime, setAfterTime);
    },
    [setAfterTime, setBeforeTime],
  );

  const searchStringSetter = useCallback(
    (value) => {
      setSearchString(value);
    },
    [setSearchString],
  );

  return (
    <Row gutter={[10, 10]} style={{ paddingBottom: "10px" }}>
      <Col
        xs={{ span: 24, order: 1 }}
        sm={{ span: 24, order: 1 }}
        md={{ span: 12, order: 1 }}
        lg={{ span: 12, order: 1 }}
        xl={{ span: 12, order: 1 }}
      >
        <Row gutter={20}>
          <Col
            xs={{ span: 24, order: 1 }}
            sm={{ span: 24, order: 1 }}
            md={{ span: 12, order: 1 }}
            lg={{ span: 12, order: 1 }}
          >
            <Search setSearchString={searchStringSetter} />
          </Col>
          <Col
            xs={{ span: 24, order: 1 }}
            sm={{ span: 24, order: 1 }}
            md={{ span: 12, order: 1 }}
            lg={{ span: 12, order: 1 }}
          >
            {" "}
            <RangePicker
              allowClear
              allowEmpty={[true, true]}
              showTime={{ format: "HH:mm" }}
              format="YYYY-MM-DD HH:mm"
              onChange={onOk}
              size="large"
            />
          </Col>
        </Row>
      </Col>

      <Col
        xs={{ span: 24, order: 2 }}
        sm={{ span: 24, order: 2 }}
        md={{ span: 12, order: 2 }}
        lg={{ span: 12, order: 2 }}
        xl={{ span: 12 }}
      >
        <div className="controlButtons">
          <HeroButton
            color="primary"
            size="md"
            onClick={refetch}
            isDisabled={ordersRetrying.length > 0}
          >
            {t("translation:refresh")}
          </HeroButton>
          <HeroButton
            color="primary"
            size="md"
            onClick={updateTransactions}
            isDisabled={!count || ordersRetrying.length > 0}
          >
            {`${t("translation:send")} ${count}`}
          </HeroButton>
        </div>
      </Col>
    </Row>
  );
}
